// src/components/editor/StoryElementModal.jsx
//
// "Describe & connect" — creates one scene, actor or artifact and, optionally,
// its first relationship in the same step.
//
// Connecting at creation time rather than afterwards is the prototype's
// structure and it earns its place: an element created in isolation has no
// place in the manuscript, so the Elements list would fill with unplaced rows
// that someone has to go back and wire up. The relationship label is free text
// on purpose — "appears in", "rivals", "is hidden in" are the author's
// vocabulary, not a taxonomy the editor should impose.
//
// Chapters are NOT creatable here. They come from the manuscript's headings, so
// "+ Chapter" inserts a real heading through the editor instead — see
// StoryElementsPanel's add menu.
import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, TextField, MenuItem, Alert,
} from '@mui/material';
import { storyElementApi } from '../../utils/readerApi';

const GOLD = '#F5D76E';

export const ELEMENT_LABELS = {
  scene: 'Scene',
  actor: 'Actor',
  artifact: 'Artifact',
};

const PLACEHOLDER = {
  scene: 'Where does this happen?',
  actor: 'Who are they, and what do they want?',
  artifact: 'What is it, and why does it matter?',
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    color: 'white',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
    '&.Mui-focused fieldset': { borderColor: GOLD },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
  '& .MuiInputLabel-root.Mui-focused': { color: GOLD },
  '& .MuiInputBase-input': { colorScheme: 'dark' },
};

export default function StoryElementModal({
  open,
  type,
  draftId,
  nodes = [],
  outline = [],
  onClose,
  onCreated,
}) {
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [connectTo, setConnectTo] = useState('');
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Reset on each open so a cancelled draft never leaks into the next element.
  useEffect(() => {
    if (open) {
      setName('');
      setDesc('');
      setConnectTo('');
      setLabel('');
      setError('');
    }
  }, [open, type]);

  // Everything an edge may point at: the elements already on the board, and
  // every chapter in the manuscript (addressed by its heading's block id).
  const connectOptions = useMemo(() => [
    ...outline
      .filter((n) => n.blockId)
      .map((n) => ({
        value: storyElementApi.ref.chapter(n.blockId),
        label: `Chapter: ${n.text || 'Untitled'}`,
      })),
    ...nodes.map((n) => ({
      value: storyElementApi.ref.node(n._id),
      label: `${ELEMENT_LABELS[n.type] || n.type}: ${n.name}`,
    })),
  ], [nodes, outline]);

  const typeLabel = ELEMENT_LABELS[type] || type;

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Give it a name first.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const node = await storyElementApi.createNode(draftId, {
        type, name: trimmed, desc: desc.trim(),
      });
      let edge = null;
      if (connectTo) {
        // The element exists either way: a relationship that fails to save is
        // worth reporting, but it must not discard what the author just typed.
        edge = await storyElementApi.createEdge(draftId, {
          from: storyElementApi.ref.node(node._id),
          to: connectTo,
          label: label.trim() || 'connected to',
        });
      }
      onCreated?.({ node, edge });
      onClose?.();
    } catch (err) {
      setError(err?.message || `Could not create this ${typeLabel.toLowerCase()}.`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { bgcolor: '#2d2d44', color: 'white', borderRadius: 2 } }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid rgba(255,255,255,0.1)', pb: 1.5 }}>
        <Typography sx={{
          color: GOLD, fontSize: 10.5, fontWeight: 700,
          letterSpacing: '0.1em', textTransform: 'uppercase',
        }}>
          New {typeLabel}
        </Typography>
        <Typography variant="h6" sx={{ mt: 0.25 }}>Describe &amp; connect</Typography>
      </DialogTitle>

      <DialogContent sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}

        <TextField
          autoFocus
          size="small"
          fullWidth
          label="Name"
          placeholder={`${typeLabel} name`}
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={fieldSx}
        />

        <TextField
          size="small"
          fullWidth
          multiline
          minRows={2}
          label="Description"
          placeholder={PLACEHOLDER[type] || ''}
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          sx={fieldSx}
        />

        <TextField
          select
          size="small"
          fullWidth
          label="Connect to (optional)"
          value={connectTo}
          onChange={(e) => setConnectTo(e.target.value)}
          sx={fieldSx}
          SelectProps={{
            MenuProps: { PaperProps: { sx: { bgcolor: '#2d2d44', color: 'white' } } },
          }}
        >
          <MenuItem value="">No connection yet</MenuItem>
          {connectOptions.map((o) => (
            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
          ))}
        </TextField>

        {/* Only meaningful once there is something to describe the link to. */}
        {connectTo && (
          <TextField
            size="small"
            fullWidth
            label="Relationship"
            placeholder="e.g. appears in, rivals, is hidden in"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            sx={fieldSx}
          />
        )}

        {connectOptions.length === 0 && (
          <Box sx={{
            p: 1.25, borderRadius: 1, border: '1px dashed rgba(255,255,255,0.2)',
            color: 'rgba(255,255,255,0.55)', fontSize: 12, lineHeight: 1.6,
          }}>
            There is nothing to connect to yet. Add a chapter or another element,
            then connect them from the Elements list.
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid rgba(255,255,255,0.1)', p: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ color: 'rgba(255,255,255,0.7)' }}>
          Cancel
        </Button>
        <Button onClick={save} disabled={saving} variant="contained" color="secondary">
          {saving ? 'Creating…' : `Create ${typeLabel}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
